import React, { useState, useEffect } from 'react';
import { FiCalendar, FiFileText, FiDownload } from 'react-icons/fi';
import { resultsAPI } from '../../services/api';

const MetadataPanel = ({ student }) => {
  const [pdfLinks, setPdfLinks] = useState(null);
  const [loadingPDFs, setLoadingPDFs] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Handle both old format (surveyMetadata) and new format (top-level fields)
  const getSurveyVersion = () => {
    if (student.surveyMetadata?.surveyVersion) {
      return student.surveyMetadata.surveyVersion;
    }
    return 'AI-v1.0';
  };

  const getCompletedDate = () => {
    if (student.completedAt) return student.completedAt;
    if (student.surveyMetadata?.completedAt) return student.surveyMetadata.completedAt;
    if (student.createdAt) return student.createdAt;
    return null;
  };

  useEffect(() => {
    if (!student?.studentID) return;

    const fetchPDFLinks = async () => {
      try {
        setLoadingPDFs(true);
        const result = await resultsAPI.getPDFLinks(student.studentID);
        if (result.success) {
          setPdfLinks(result.data);
        }
      } catch {
        // S3 not configured or no PDFs — silently ignore
      } finally {
        setLoadingPDFs(false);
      }
    };

    fetchPDFLinks();
  }, [student?.studentID]);

  const hasPDFs = pdfLinks && (pdfLinks.current || pdfLinks.history?.length > 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Survey Information</h2>

      <div className="space-y-4">
        <div className="flex items-start">
          <FiFileText className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
          <div>
            <p className="text-sm text-gray-600">Survey Version</p>
            <p className="text-base font-medium text-gray-900">{getSurveyVersion()}</p>
          </div>
        </div>

        <div className="flex items-start">
          <FiCalendar className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
          <div>
            <p className="text-sm text-gray-600">Completed At</p>
            <p className="text-base font-medium text-gray-900">
              {formatDate(getCompletedDate())}
            </p>
          </div>
        </div>
      </div>

      {/* PDF Reports Section */}
      {!loadingPDFs && hasPDFs && (
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="flex items-center mb-3">
            <FiDownload className="h-4 w-4 text-gray-400 mr-2" />
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Survey PDF Reports
            </h3>
          </div>

          <div className="space-y-2">
            {/* Current (most recent) survey PDF */}
            {pdfLinks.current && (
              <a
                href={pdfLinks.current.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors group"
              >
                <div>
                  <span className="text-sm font-medium text-purple-800">
                    {pdfLinks.current.gradeLevel
                      ? `Grade ${pdfLinks.current.gradeLevel}`
                      : 'Current Survey'}
                  </span>
                  <span className="text-xs text-purple-500 ml-2">
                    {formatShortDate(pdfLinks.current.completedAt)}
                  </span>
                  <span className="ml-2 text-xs font-medium text-purple-600 bg-purple-100 group-hover:bg-purple-200 px-1.5 py-0.5 rounded">
                    Latest
                  </span>
                </div>
                <FiDownload className="h-4 w-4 text-purple-400 group-hover:text-purple-600 flex-shrink-0" />
              </a>
            )}

            {/* Historical PDFs */}
            {pdfLinks.history?.map((item, idx) => (
              <a
                key={idx}
                href={item.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
              >
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {item.gradeLevel ? `Grade ${item.gradeLevel}` : 'Previous Survey'}
                  </span>
                  <span className="text-xs text-gray-400 ml-2">
                    {formatShortDate(item.completedAt)}
                  </span>
                </div>
                <FiDownload className="h-4 w-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataPanel;
